import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Mail, Menu, X } from 'lucide-react';
import { NavLink, useLocation } from 'react-router-dom';
import Button from './Button';
import { scrollToAnchorAfterLayout } from '../utils/scrollToAnchor';
import { company, homeImages, links } from '../data/siteData';

const CONTACT_NAV_TARGET = '/contact#contact-form';
const BOOKING_NAV_TARGET = '/contact#booking';

function getNavTarget(item) {
  return item.path === '/contact' ? CONTACT_NAV_TARGET : item.path;
}

function isItemActive(item, currentPath) {
  if (item.path === currentPath) return true;
  if (item.children?.some((child) => child.path === currentPath)) return true;
  if (item.path !== '/' && currentPath.startsWith(item.path + '/')) return true;
  return false;
}

export default function Navbar({ items, ctaLabel }) {
  const [open, setOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRef = useRef(null);
  const { pathname } = useLocation();

  const handleContactNavClick = (item, closeMobile = false) => {
    if (closeMobile) {
      setOpen(false);
    }

    if (item.path === '/contact' && pathname === '/contact') {
      scrollToAnchorAfterLayout('contact-form');
    }
  };

  const handleBookingCtaClick = (closeMobile = false) => {
    if (closeMobile) {
      setOpen(false);
    }

    if (pathname === '/contact') {
      scrollToAnchorAfterLayout('booking');
    }
  };

  // Close dropdown when route changes
  useEffect(() => {
    setOpenDropdown(null);
    setOpen(false);
  }, [pathname]);

  // Close dropdown on outside click or Escape
  useEffect(() => {
    if (!openDropdown) return;

    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setOpenDropdown(null);
    };

    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [openDropdown]);

  return (
    <header className="site-header">
      <div className="utility-bar">
        <div className="shell utility-bar__row">
          <a href={`mailto:${company.email}`} className="utility-link">
            <Mail size={15} /> {company.email}
          </a>
          {links.linkedin ? (
            <>
              <span className="utility-divider" aria-hidden="true" />
              <a href={links.linkedin} className="utility-link" aria-label="BrainTorch™ Bio LinkedIn" target="_blank" rel="noopener noreferrer">
                in
              </a>
            </>
          ) : null}
        </div>
      </div>

      <div className="navbar">
        <div className="shell navbar__row">
          <NavLink to="/" className="brand brand--with-logo" onClick={() => setOpen(false)}>
            <img src={homeImages.symbol} alt="BrainTorch™ Bio torch symbol" className="brand__logo" />
            <span className="brand__text">
              <span className="brand__name">{company.name}</span>
              <span className="brand__eyebrow">{company.eyebrow}</span>
            </span>
          </NavLink>

          <nav className="nav-links nav-links--desktop" aria-label="Primary navigation" ref={dropdownRef}>
            {items.map((item) => {
              if (item.children?.length) {
                const isOpen = openDropdown === item.path;
                const active = isItemActive(item, pathname);
                return (
                  <div key={item.path} className="nav-dropdown">
                    <button
                      type="button"
                      className={`nav-link nav-dropdown__trigger ${active ? 'nav-link--active' : ''}`}
                      aria-haspopup="true"
                      aria-expanded={isOpen}
                      onClick={() => setOpenDropdown(isOpen ? null : item.path)}
                    >
                      {item.label}
                      <ChevronDown size={14} className={`nav-dropdown__chevron ${isOpen ? 'nav-dropdown__chevron--open' : ''}`} />
                    </button>
                    {isOpen && (
                      <div className="nav-dropdown__menu" role="menu">
                        <NavLink
                          to={item.path}
                          end
                          className={({ isActive }) => `nav-dropdown__item ${isActive ? 'nav-dropdown__item--active' : ''}`}
                          role="menuitem"
                        >
                          {item.label} Overview
                        </NavLink>
                        {item.children.map((child) => (
                          <NavLink
                            key={child.path}
                            to={child.path}
                            className={({ isActive }) => `nav-dropdown__item ${isActive ? 'nav-dropdown__item--active' : ''}`}
                            role="menuitem"
                          >
                            {child.label}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <NavLink
                  key={item.path}
                  to={getNavTarget(item)}
                  end={item.path === '/'}
                  className={({ isActive }) => `nav-link ${isActive ? 'nav-link--active' : ''}`}
                  onClick={() => handleContactNavClick(item)}
                >
                  {item.label}
                </NavLink>
              );
            })}
          </nav>

          {ctaLabel ? (
            <div className="nav-links--desktop">
              <Button to={BOOKING_NAV_TARGET} onClick={() => handleBookingCtaClick()}>{ctaLabel}</Button>
            </div>
          ) : null}

          <button className="mobile-toggle" onClick={() => setOpen((prev) => !prev)} aria-label="Toggle menu">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {open && (
          <div className="mobile-panel">
            <div className="shell mobile-panel__inner">
              {items.map((item) => (
                <div key={item.path}>
                  <NavLink
                    to={getNavTarget(item)}
                    end={item.path === '/'}
                    className={({ isActive }) => `mobile-link ${isActive ? 'mobile-link--active' : ''}`}
                    onClick={() => handleContactNavClick(item, true)}
                  >
                    {item.label}
                  </NavLink>
                  {item.children?.map((child) => (
                    <NavLink
                      key={child.path}
                      to={child.path}
                      className={({ isActive }) => `mobile-link mobile-link--sub ${isActive ? 'mobile-link--active' : ''}`}
                      onClick={() => setOpen(false)}
                    >
                      {child.label}
                    </NavLink>
                  ))}
                </div>
              ))}
              {ctaLabel ? (
                <Button to={BOOKING_NAV_TARGET} onClick={() => handleBookingCtaClick(true)}>
                  {ctaLabel}
                </Button>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
